import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface ServiceOption {
  id: number;
  name: string;
  type: 'visa_pack' | 'trip_booking' | 'rental_car';
  price?: number;
  currency?: string;
  code?: string;
}

interface InvoiceLineItem {
  key: string;
  service_type: 'visa_pack' | 'trip_booking' | 'rental_car' | '';
  service_id: string;
  service_code: string;
  description: string;
  unit: string;
  quantity: string;
  unit_price: string;
}

interface Invoice {
  id: number;
  reference: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string;
  service_type: 'visa_pack' | 'trip_booking' | 'rental_car';
  service_id: number;
  items?: InvoiceLineItem[];
  amount: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  issue_date: string;
  due_date: string;
  paid_date?: string;
  notes?: string;
  invoice_data?: {
    service_name?: string;
    tax_rate?: number;
  };
}

const newKey = () => Math.random().toString(36).slice(2);

const emptyLine = (): InvoiceLineItem => ({
  key: newKey(),
  service_type: "",
  service_id: "",
  service_code: "",
  description: "",
  unit: "service",
  quantity: "1",
  unit_price: "",
});

const CURRENCIES: { code: string; label: string }[] = [
  { code: "OMR", label: "OMR - Omani Rial" },
  { code: "AED", label: "AED - UAE Dirham" },
  { code: "SAR", label: "SAR - Saudi Riyal" },
  { code: "QAR", label: "QAR - Qatari Riyal" },
  { code: "KWD", label: "KWD - Kuwaiti Dinar" },
  { code: "BHD", label: "BHD - Bahraini Dinar" },
  { code: "USD", label: "USD - US Dollar" },
  { code: "EUR", label: "EUR - Euro" },
  { code: "GBP", label: "GBP - British Pound" },
  { code: "JOD", label: "JOD - Jordanian Dinar" },
  { code: "EGP", label: "EGP - Egyptian Pound" },
  { code: "LBP", label: "LBP - Lebanese Pound" },
  { code: "IQD", label: "IQD - Iraqi Dinar" },
  { code: "YER", label: "YER - Yemeni Rial" },
  { code: "INR", label: "INR - Indian Rupee" },
  { code: "PKR", label: "PKR - Pakistani Rupee" },
  { code: "BDT", label: "BDT - Bangladeshi Taka" },
  { code: "LKR", label: "LKR - Sri Lankan Rupee" },
  { code: "NPR", label: "NPR - Nepalese Rupee" },
  { code: "PHP", label: "PHP - Philippine Peso" },
  { code: "CNY", label: "CNY - Chinese Yuan" },
  { code: "JPY", label: "JPY - Japanese Yen" },
  { code: "KRW", label: "KRW - South Korean Won" },
  { code: "THB", label: "THB - Thai Baht" },
  { code: "MYR", label: "MYR - Malaysian Ringgit" },
  { code: "SGD", label: "SGD - Singapore Dollar" },
  { code: "IDR", label: "IDR - Indonesian Rupiah" },
  { code: "VND", label: "VND - Vietnamese Dong" },
  { code: "TRY", label: "TRY - Turkish Lira" },
  { code: "RUB", label: "RUB - Russian Ruble" },
  { code: "ZAR", label: "ZAR - South African Rand" },
  { code: "NGN", label: "NGN - Nigerian Naira" },
  { code: "KES", label: "KES - Kenyan Shilling" },
  { code: "MAD", label: "MAD - Moroccan Dirham" },
  { code: "TND", label: "TND - Tunisian Dinar" },
  { code: "DZD", label: "DZD - Algerian Dinar" },
  { code: "CAD", label: "CAD - Canadian Dollar" },
  { code: "AUD", label: "AUD - Australian Dollar" },
  { code: "NZD", label: "NZD - New Zealand Dollar" },
  { code: "CHF", label: "CHF - Swiss Franc" },
  { code: "SEK", label: "SEK - Swedish Krona" },
  { code: "NOK", label: "NOK - Norwegian Krone" },
  { code: "DKK", label: "DKK - Danish Krone" },
  { code: "PLN", label: "PLN - Polish Zloty" },
  { code: "CZK", label: "CZK - Czech Koruna" },
  { code: "HUF", label: "HUF - Hungarian Forint" },
  { code: "BRL", label: "BRL - Brazilian Real" },
  { code: "MXN", label: "MXN - Mexican Peso" },
];

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  cancelBg: "#d3be76",
  cancelHover: "#c2ad65",
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: '8px',
  fontSize: '13px',
  backgroundColor: colors.bgSubtle,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: colors.textMuted,
  display: 'block',
  marginBottom: '6px'
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div style={{
                width: 150,
                height: 28,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: 60,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div style={{
              width: 280,
              height: 18,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-3">
            <div style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "16px",
            }}>
              <div style={{
                width: 120,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: "100%",
                height: 80,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "24px",
            }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ marginBottom: i < 3 ? "20px" : 0 }}>
                  <div style={{
                    width: 140,
                    height: 20,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 12,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    {[...Array(i === 0 ? 2 : i === 1 ? 2 : i === 2 ? 3 : 1)].map((_, j) => (
                      <div key={j}>
                        <div style={{
                          width: 80,
                          height: 12,
                          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                          backgroundSize: "200% 100%",
                          borderRadius: 4,
                          marginBottom: 6,
                          animation: "shimmer 1.5s infinite"
                        }}></div>
                        <div style={{
                          width: "100%",
                          height: 42,
                          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                          backgroundSize: "200% 100%",
                          borderRadius: 8,
                          animation: "shimmer 1.5s infinite"
                        }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "16px",
            }}>
              <div style={{
                width: 100,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: "100%",
                height: 38,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const EditInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    currency: "OMR",
    tax_rate: "0",
    status: "pending" as 'pending' | 'paid' | 'overdue' | 'cancelled',
    due_date: "",
    paid_date: "",
    notes: ""
  });

  const [items, setItems] = useState<InvoiceLineItem[]>([emptyLine()]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [originalInvoice, setOriginalInvoice] = useState<Invoice | null>(null);

  // Fetch des services - MÊME CODE QUE AddInvoice
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoadingServices(true);

        // ✅ Récupérer tous les packs actifs - identique à AddInvoice
        const response = await axios.get(`${API_URL}/visa-packs`, {
          params: { per_page: 100 },
          ...config_token
        });
        
        const packs = response.data?.data?.data || response.data?.data || [];
        
        const visaPacks: ServiceOption[] = packs.map((pack: any) => ({
          id: pack.id,
          name: pack.name_en || pack.name || `Visa Pack #${pack.id}`,
          type: "visa_pack" as const,
          price: pack.price !== null && pack.price !== undefined ? Number(pack.price) : undefined,
          currency: pack.currency || 'OMR',
          code: `VP-${pack.id}`,
        }));

        const mockTripAndCarServices: ServiceOption[] = [
          { id: 101, name: "Paris City Tour - 3 Days", type: "trip_booking", price: 450, code: "TB-101" },
          { id: 102, name: "Bali Adventure Package", type: "trip_booking", price: 750, code: "TB-102" },
          { id: 103, name: "Tokyo Cultural Experience", type: "trip_booking", price: 600, code: "TB-103" },
          { id: 201, name: "Toyota Corolla - 7 Days", type: "rental_car", price: 280, code: "RC-201" },
          { id: 202, name: "BMW X5 - Luxury SUV", type: "rental_car", price: 650, code: "RC-202" },
          { id: 203, name: "Honda Civic - Compact", type: "rental_car", price: 220, code: "RC-203" },
        ];

        setServices([...visaPacks, ...mockTripAndCarServices]);
        
        if (visaPacks.length === 0) {
          toast.info("No active visa packs found. Please create some first.");
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to load services");
        
        const fallbackServices: ServiceOption[] = [
          { id: 1, name: "Oman Tourist Visa - 10 Days", type: "visa_pack", price: 45, currency: "OMR", code: "VP-1" },
          { id: 2, name: "UAE Tourist Visa - 30 Days", type: "visa_pack", price: 120, currency: "AED", code: "VP-2" },
          { id: 101, name: "Paris City Tour - 3 Days", type: "trip_booking", price: 450, code: "TB-101" },
          { id: 102, name: "Bali Adventure Package", type: "trip_booking", price: 750, code: "TB-102" },
          { id: 201, name: "Toyota Corolla - 7 Days", type: "rental_car", price: 280, code: "RC-201" },
        ];
        setServices(fallbackServices);
      } finally {
        setIsLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;

      try {
        setIsLoadingInvoice(true);
        const response = await axios.get(`${API_URL}/invoices/${id}`, config_token);

        if (response.data.status && response.data.data) {
          const invoice: Invoice = response.data.data;
          setOriginalInvoice(invoice);

          const taxRate = invoice.invoice_data?.tax_rate !== undefined
            ? String(invoice.invoice_data.tax_rate)
            : (invoice.tax_amount && invoice.amount
              ? ((invoice.tax_amount / invoice.amount) * 100).toFixed(0)
              : "0");

          setFormData({
            customer_name: invoice.customer_name || "",
            customer_email: invoice.customer_email || "",
            customer_phone: invoice.customer_phone || "",
            customer_address: invoice.customer_address || "",
            currency: invoice.currency || "OMR",
            tax_rate: taxRate,
            status: invoice.status || "pending",
            due_date: invoice.due_date ? invoice.due_date.split('T')[0] : "",
            paid_date: invoice.paid_date ? invoice.paid_date.split('T')[0] : "",
            notes: invoice.notes || ""
          });

          if (invoice.items && invoice.items.length > 0) {
            setItems(invoice.items.map((it: any) => ({
              key: newKey(),
              service_type: it.service_type,
              service_id: String(it.service_id),
              service_code: it.service_code || "",
              description: it.description,
              unit: it.unit || "service",
              quantity: String(it.quantity),
              unit_price: String(it.unit_price),
            })));
          } else {
            setItems([{
              key: newKey(),
              service_type: invoice.service_type || "",
              service_id: invoice.service_id ? String(invoice.service_id) : "",
              service_code: "",
              description: invoice.invoice_data?.service_name || "",
              unit: "service",
              quantity: "1",
              unit_price: invoice.amount ? String(invoice.amount) : "",
            }]);
          }
        } else {
          toast.error("Invoice not found");
          navigate("/admin/list-invoices");
        }
      } catch (error: any) {
        console.error("Error fetching invoice:", error);
        toast.error("Failed to load invoice");
        navigate("/admin/list-invoices");
      } finally {
        setIsLoadingInvoice(false);
      }
    };

    fetchInvoice();
  }, [id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === "status" && value === "paid" && !prev.paid_date) {
        next.paid_date = new Date().toISOString().split('T')[0];
      }
      return next;
    });
  };

  const addLine = () => setItems(prev => [...prev, emptyLine()]);

  const removeLine = (key: string) => {
    setItems(prev => (prev.length > 1 ? prev.filter(i => i.key !== key) : prev));
  };

  const updateLine = (key: string, field: keyof InvoiceLineItem, value: string) => {
    setItems(prev => {
      const isFirstLine = prev.findIndex(l => l.key === key) === 0;

      return prev.map(line => {
        if (line.key !== key) return line;
        const next = { ...line, [field]: value };

        if (field === "service_type") {
          next.service_id = "";
          next.service_code = "";
          next.description = "";
          next.unit_price = "";
        }

        if (field === "service_id" && value) {
          const selected = services.find(s => String(s.id) === value);
          if (selected) {
            next.description = selected.name;
            next.service_code = selected.code || "";
            if (selected.price !== undefined) next.unit_price = String(selected.price);
            if (isFirstLine && selected.currency) {
              setFormData(fd => ({ ...fd, currency: selected.currency! }));
            }
          }
        }

        return next;
      });
    });
  };

  const filteredServicesFor = (type: string) => services.filter(s => s.type === type);

  const lineAmount = (line: InvoiceLineItem) => {
    const qty = parseFloat(line.quantity || "0");
    const price = parseFloat(line.unit_price || "0");
    return qty > 0 && price >= 0 ? qty * price : 0;
  };

  const subtotal = items.reduce((sum, line) => sum + lineAmount(line), 0);
  const taxRateNum = parseFloat(formData.tax_rate || "0");
  const taxAmount = subtotal * taxRateNum / 100;
  const total = subtotal + taxAmount;

  const validateForm = () => {
    if (!formData.customer_name.trim()) {
      toast.error("Customer name is required");
      return false;
    }
    if (!formData.customer_email.trim()) {
      toast.error("Customer email is required");
      return false;
    }
    if (!formData.due_date) {
      toast.error("Due date is required");
      return false;
    }
    if (items.length === 0) {
      toast.error("Add at least one invoice line");
      return false;
    }
    for (const line of items) {
      if (!line.service_type) { toast.error("Every line needs a service type"); return false; }
      if (!line.service_id) { toast.error("Every line needs a service selected"); return false; }
      if (!line.quantity || parseFloat(line.quantity) <= 0) { toast.error("Every line needs a valid quantity"); return false; }
      if (line.unit_price === "" || parseFloat(line.unit_price) < 0) { toast.error("Every line needs a valid unit price"); return false; }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const submitData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        currency: formData.currency,
        tax_rate: taxRateNum,
        status: formData.status,
        due_date: formData.due_date,
        paid_date: formData.paid_date || null,
        notes: formData.notes,
        items: items.map(line => ({
          service_type: line.service_type,
          service_id: parseInt(line.service_id),
          service_code: line.service_code || undefined,
          description: line.description,
          unit: line.unit || "service",
          quantity: parseFloat(line.quantity),
          unit_price: parseFloat(line.unit_price),
        })),
      };

      const response = await axios.put(`${API_URL}/invoices/${id}`, submitData, config_token);

      if (response.data.status) {
        toast.success("Invoice updated successfully!");
        navigate("/admin/list-invoices");
      }
    } catch (error: any) {
      console.error("Error updating invoice:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to update invoice");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (!originalInvoice) return;
    const invoice = originalInvoice;

    const taxRate = invoice.invoice_data?.tax_rate !== undefined
      ? String(invoice.invoice_data.tax_rate)
      : (invoice.tax_amount && invoice.amount ? ((invoice.tax_amount / invoice.amount) * 100).toFixed(0) : "0");

    setFormData({
      customer_name: invoice.customer_name || "",
      customer_email: invoice.customer_email || "",
      customer_phone: invoice.customer_phone || "",
      customer_address: invoice.customer_address || "",
      currency: invoice.currency || "OMR",
      tax_rate: taxRate,
      status: invoice.status || "pending",
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : "",
      paid_date: invoice.paid_date ? invoice.paid_date.split('T')[0] : "",
      notes: invoice.notes || ""
    });

    if (invoice.items && invoice.items.length > 0) {
      setItems(invoice.items.map((it: any) => ({
        key: newKey(),
        service_type: it.service_type,
        service_id: String(it.service_id),
        service_code: it.service_code || "",
        description: it.description,
        unit: it.unit || "service",
        quantity: String(it.quantity),
        unit_price: String(it.unit_price),
      })));
    } else {
      setItems([emptyLine()]);
    }
  };

  if (isLoadingInvoice) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* EN-TÊTE */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px 0", color: colors.primary, letterSpacing: "-0.02em" }}>
                Edit Invoice
              </h1>
              <p style={{ fontSize: 13.5, color: colors.textMuted, margin: 0 }}>
                Update invoice details for reference: <strong style={{ color: colors.primary }}>{originalInvoice?.reference}</strong>
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/list-invoices")}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                fontSize: "13.5px",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#0f172a";
                e.currentTarget.style.borderColor = "#94a3b8";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Invoices
            </button>
          </div>

          <div className="row g-4">
            {/* Colonne Gauche - Summary */}
            <div className="col-12 col-lg-3">
              <div
                style={{
                  background: "#fff",
                  border: `0.5px solid ${colors.borderLight}`,
                  borderRadius: 12,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  position: "sticky",
                  top: "20px",
                }}
              >
                <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: colors.text }}>Invoice Summary</h2>
                <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, lineHeight: 1.4 }}>
                  Review changes before updating {originalInvoice?.reference}.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 12, background: colors.bgSubtle, padding: 12, borderRadius: 8 }}>
                  <div>
                    <span style={{ color: colors.textFaint }}>Customer:</span>
                    <div style={{ fontWeight: 600, color: colors.text }}>{formData.customer_name || "N/A"}</div>
                  </div>
                  <div>
                    <span style={{ color: colors.textFaint }}>Lines:</span>
                    <div style={{ fontWeight: 600, color: colors.text }}>
                      {items.filter(l => l.description).length || 0} item{items.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div style={{ borderTop: `1px solid ${colors.borderLight}`, paddingTop: 8, marginTop: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Subtotal:</span>
                      <span>{formData.currency} {subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Tax ({taxRateNum}%):</span>
                      <span>{formData.currency} {taxAmount.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 6, color: colors.primary }}>
                      <span>Total:</span>
                      <span>{formData.currency} {total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Colonne Centre - Formulaire */}
            <div className="col-12 col-lg-6">
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {/* Customer Information */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Customer Information</h3>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Customer Name *</label>
                      <input type="text" name="customer_name" placeholder="Enter customer full name" value={formData.customer_name} onChange={handleInputChange} required style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Customer Email *</label>
                      <input type="email" name="customer_email" placeholder="customer@example.com" value={formData.customer_email} onChange={handleInputChange} required style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Customer Phone</label>
                      <input type="tel" name="customer_phone" placeholder="+1 234 567 8900" value={formData.customer_phone} onChange={handleInputChange} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Currency *</label>
                      <select name="currency" value={formData.currency} onChange={handleInputChange} required style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}>
                        {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Customer Address</label>
                    <textarea name="customer_address" placeholder="Enter customer full address" value={formData.customer_address} onChange={handleInputChange} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                </div>

                {/* Status & Dates */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Status & Dates</h3>

                  <div style={{ display: "grid", gridTemplateColumns: formData.status === 'paid' ? "1fr 1fr 1fr" : "1fr 1fr", gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Due Date *</label>
                      <input type="date" name="due_date" value={formData.due_date} onChange={handleInputChange} required style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Status *</label>
                      <select name="status" value={formData.status} onChange={handleInputChange} required style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    {formData.status === 'paid' && (
                      <div>
                        <label style={labelStyle}>Paid Date</label>
                        <input type="date" name="paid_date" value={formData.paid_date} onChange={handleInputChange} style={inputStyle} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Lines */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Invoice Lines</h3>
                    {isLoadingServices && <span style={{ fontSize: 11, color: colors.textFaint }}>Loading services...</span>}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {items.map((line, index) => {
                      const options = filteredServicesFor(line.service_type);
                      const amount = lineAmount(line);
                      return (
                        <div
                          key={line.key}
                          style={{
                            border: `1px solid ${colors.borderLight}`,
                            borderRadius: 10,
                            padding: 14,
                            background: colors.bgSubtle,
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: colors.textFaint, letterSpacing: "0.04em" }}>LINE {index + 1}</span>
                            {items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLine(line.key)}
                                style={{ background: "none", border: "none", color: colors.danger, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <label style={labelStyle}>Service Type *</label>
                              <select
                                value={line.service_type}
                                onChange={(e) => updateLine(line.key, "service_type", e.target.value)}
                                style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}
                              >
                                <option value="">Select Service Type</option>
                                <option value="visa_pack">Visa Pack</option>
                                <option value="trip_booking">Trip Booking</option>
                                <option value="rental_car">Rental Car</option>
                              </select>
                            </div>
                            <div>
                              <label style={labelStyle}>Service *</label>
                              <select
                                value={line.service_id}
                                onChange={(e) => updateLine(line.key, "service_id", e.target.value)}
                                disabled={!line.service_type || isLoadingServices}
                                style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}
                              >
                                <option value="">Select Service</option>
                                {options.map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.name} {s.price !== undefined && `- ${s.currency || formData.currency} ${s.price}`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label style={labelStyle}>Description</label>
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => updateLine(line.key, "description", e.target.value)}
                              placeholder="Auto-filled from the selected service, editable"
                              style={inputStyle}
                            />
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                            <div>
                              <label style={labelStyle}>Quantity *</label>
                              <input
                                type="number" min="0.01" step="0.01"
                                value={line.quantity}
                                onChange={(e) => updateLine(line.key, "quantity", e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div>
                              <label style={labelStyle}>Unit Price *</label>
                              <input
                                type="number" min="0" step="0.001"
                                value={line.unit_price}
                                onChange={(e) => updateLine(line.key, "unit_price", e.target.value)}
                                style={inputStyle}
                              />
                            </div>
                            <div>
                              <label style={labelStyle}>Line Amount</label>
                              <input type="text" readOnly value={`${formData.currency} ${amount.toFixed(2)}`} style={{ ...inputStyle, fontWeight: 600, color: colors.primary }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={addLine}
                    style={{
                      alignSelf: "flex-start",
                      padding: "8px 16px",
                      borderRadius: 8,
                      border: `1px dashed ${colors.primary}`,
                      background: "#fff",
                      color: colors.primary,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    + Add Line
                  </button>
                </div>

                {/* Tax & Totals */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Tax & Totals</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Tax Rate (%)</label>
                      <select name="tax_rate" value={formData.tax_rate} onChange={handleInputChange} style={{ ...inputStyle, cursor: 'pointer', backgroundColor: '#fff' }}>
                        <option value="0">No Tax (0%)</option>
                        <option value="5">Standard (5%)</option>
                        <option value="10">Reduced (10%)</option>
                        <option value="15">Premium (15%)</option>
                        <option value="20">High (20%)</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Subtotal</label>
                      <input type="text" readOnly value={`${formData.currency} ${subtotal.toFixed(2)}`} style={{ ...inputStyle, backgroundColor: colors.bgSubtle }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Total Amount</label>
                      <input type="text" readOnly value={`${formData.currency} ${total.toFixed(2)}`} style={{ ...inputStyle, backgroundColor: colors.bgSubtle, fontWeight: 700, color: colors.primary }} />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text }}>Additional Information</h3>
                  <div>
                    <label style={labelStyle}>Notes</label>
                    <textarea name="notes" placeholder="Additional notes or terms and conditions..." value={formData.notes} onChange={handleInputChange} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                </div>

                {/* Form Actions */}
                <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button type="button" onClick={handleReset} style={{ padding: "8px 16px", backgroundColor: colors.bgSubtle, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                    Reset
                  </button>
                  <button type="button" onClick={() => navigate("/admin/list-invoices")} style={{ padding: "8px 16px", backgroundColor: colors.cancelBg, color: "#fff", border: 'none', borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isLoading} style={{ padding: "8px 20px", backgroundColor: colors.primary, color: "#fff", border: "none", borderRadius: 8, cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, opacity: isLoading ? 0.6 : 1 }}>
                    {isLoading ? "Updating..." : "Update Invoice"}
                  </button>
                </div>

              </form>
            </div>

            {/* Colonne Droite - Quick Actions */}
            <div className="col-12 col-lg-3">
              <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 12, position: "sticky", top: "20px" }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: colors.text }}>Quick Actions</h3>
                <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>Return safely to the invoices list.</p>
                <button
                  type="button"
                  onClick={() => navigate("/admin/list-invoices")}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${colors.border}`, background: "#fff", color: colors.text, fontSize: 12, fontWeight: 500, cursor: "pointer" }}
                >
                  Back to Invoices
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default EditInvoice;